import { ecId, ecLean, definitionNotHeld, type JanusReaders, type LogRow, type Row } from '../ability/context';
import { readItemField } from '../compile/item-field-reader';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { fromBaseRecord, fromBaseUuid } from '../compile/from-base-context';
import { creatureBodyVanishesActive } from '../port/k4-dispatch/kill_or_vanish_creature';
import type { DispatchEnv } from '../port/k4-dispatch/dispatch';
import { isAnimatedSummon, summonControllerId } from './summons';

type DeathReaders = Pick<JanusReaders, 'value' | 'has' | 'entries' | 'definition'> & {
    triggerConditionsMet?: (ability: Row, caster: Row) => boolean;
    /** The creature's authored source row supplies its own unconditional vanish flag. */
    creatureSource?: (id: string) => Row | null;
};

/** The existing K4 port owns the complete own-definition / passive / feat walk, including live
 * conditions. Its dependencies here are held browser readers; an absent definition is fetched
 * through the ordinary named-definition mechanism, never interpreted as an absent effect. */
export function deathBodyVanishes(target: Row, read: DeathReaders): boolean {
    const row = (value: unknown): Row | null => fromBaseRecord(value) ? value : null;
    const ownId = String(readItemField(target, 'item_id') ?? ecLean(target, 'item_id') ?? '').toUpperCase();
    const definition = (id: unknown): Row => {
        const key = String(id ?? '').toUpperCase(), held = (key === ownId ? read.creatureSource?.(key) : null) ?? read.definition(key);
        if (!held) definitionNotHeld('death:body-definition-not-held', key);
        return held;
    };
    const deps = {
        check_uuid: fromBaseUuid,
        item_field: (value: unknown, key: unknown): unknown => readItemField(row(value), String(key)) ?? (row(value) ? ecLean(row(value)!, String(key)) : null),
        query_full_item_arr: definition,
        layer_value: (value: unknown, key: unknown) => read.value(row(value), String(key)),
        layer_has: (value: unknown, key: unknown) => read.has(row(value), String(key)),
        layer_entries: (value: unknown, key: unknown) => read.entries(row(value), String(key)),
        layer_list_entry_uuid: readLayerListEntryUuid,
        check_ability_conditions: (ability: unknown, owner: unknown): boolean => {
            if (!read.triggerConditionsMet) throw new Error('death:body-conditions-not-held');
            return read.triggerConditionsMet(row(ability)!, row(owner)!);
        },
    };
    // This read-only port uses only the listed dependency calls; no clock, random or output API.
    return creatureBodyVanishesActive(target, { deps } as unknown as DispatchEnv);
}

export interface DeathClassification {
    vanished: boolean;
    changes: { encounter_creature_dead: 1; v_current: 0 } | { encounter_creature_deleted: 1 };
    rows: LogRow[];
}

/** kill_or_vanish_creature's saved outcome and its two terminal log rows. Turn advancement and
 * apply_death_effect run between the mark and these logs in the common composer. */
export function classifyKillOrVanish(target: Row, applied: number, read: DeathReaders, killMessage = 'creature_killed', forceCorpse = false): DeathClassification {
    const controller = summonControllerId(target);
    const type = readItemField(target, 'item_type') ?? ecLean(target, 'item_type');
    const vanished = !forceCorpse && type === 'creature' && (!controller || isAnimatedSummon(target)) && deathBodyVanishes(target, read);
    const vitality = fromBaseRecord(target.computed) && fromBaseRecord(target.computed.vitality) ? target.computed.vitality : null;
    const max = Number(vitality?.v_max ?? ecLean(target, 'v_max'));
    if (!Number.isFinite(max)) throw new Error('death:maximum-vitality-not-held');
    const message = (kind: string): LogRow => ({ roll_type_1: kind, roll_type_2: null, roll_item_id: null,
        roll_by: ecId(target), roll_to: null, roll_result: 0 });
    return { vanished, changes: vanished ? { encounter_creature_deleted: 1 } : { encounter_creature_dead: 1, v_current: 0 },
        rows: [message(vanished ? 'creature_body_vanishes' : killMessage),
            ...(applied > 0 && applied >= Math.trunc(max) ? [message('massive_damage_instant_death')] : [])] };
}
